import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateArtifactContext } from "../../../../context/CreateArtifactContext";
import {
  ContentState,
  ContentSubstate,
  SKArtifact,
  TopicTypeString,
  getArtefactIcon,
  getContentCreatorActionableItems,
} from "../../../../models/SKArtifact";
import { useNavigationBar } from "../../../../context/NavigationContext";
import { capitalizeFirstLetter } from "../../../../utils/stringFunctions";
import * as Yup from "yup";
import ContentHistoryNavigator from "./components/ContentHistoryNavigator";

import { ArtifactContainerRef } from "./CreateArtifact";
import { AddTextField } from "../../../../components/AddTextField";
import { AddTextAreaField } from "../../../../components/AddTextAreaField";
import ContextCheckListCard from "./components/ContextCheckListCard";
import { PublishingTier } from "./components/PublishingTier";
import ContentEditNavigator from "../../../contentModeration/ContentEditNavigator";
import { ContentAgeRestriction } from "../../../../api/enums";
import ContentReportNavigator from "./components/ContentReportNavigator";
import { useAuth, UserRole } from "../../../../context/AuthContext";

// type TextErrorProps = {
//   error?: { [key: string]: string | undefined };
// };

const TextArtifactContainer = forwardRef<ArtifactContainerRef>((props, ref) => {
  //const [isValidForm, setIsValidForm] = useState(false);

  const context = useAuth();
  const [error, setErrorState] = useState<{ [key: string]: string | undefined }>({});

  const setErrors = (newErrors: { [key: string]: string | undefined }) => {
    setErrorState(newErrors);
  };

  useImperativeHandle(ref, () => ({
    containerSchema: () => schema,
    setErrors: newErrors => setErrors(newErrors),
  }));

  const { contentData, updateContentData, artifactId, isModerator, isContentEditing, isEditable } = useContext(CreateArtifactContext);

  useEffect(() => {}, []);
  const navigate = useNavigate();

  const schema = Yup.object().shape({
    type: Yup.string().required(),
    title: Yup.string().required("Title is required").max(50),
    subTitle: Yup.string().notRequired().max(500),
    text: Yup.string().notRequired().max(500),
    topic: Yup.string().required("Topic is required"),
    tier: Yup.string().required(),
    age: Yup.number().required("Age rating is required"),
    status: Yup.string().required(),
    coverMedia: Yup.object({
      coverImageURL: Yup.string().notRequired(),
      coverVideoURL: Yup.string().notRequired(),
      coverAudioURL: Yup.string().notRequired(),
    }).notRequired(),
    certifyFirst: Yup.boolean().oneOf([true]).required("Please certify content"),
    certifySecond: Yup.boolean().oneOf([true]).required("Please certify content"),
  });

  // useEffect(() => {
  //   setNavProps({
  //     backArrow: true,
  //     title: "Create",
  //     subtitle: capitalizeFirstLetter(contentData.type ?? ""),
  //     icon: contentData.type ? getArtefactIcon(contentData.type) : undefined,
  //     buttonTitle: containerButtonTitle(),
  //     filterOption: false,
  //     enableLink: contentData.status === ContentState.IN_REVIEW ? false : isValidForm,
  //     onFilter: () => {},
  //     onNext: submit,
  //   });
  // }, [isValidForm, contentData]);

  return (
    <>
      <div className="artifact-page-context">
        {isModerator &&
        !isContentEditing &&
        artifactId &&
        contentData.status === ContentState.ACTIVE &&
        contentData.subStatus === ContentSubstate.EDIT_IN_REVIEW ? (
          <ContentEditNavigator title="Channel Edit" docId={artifactId!} />
        ) : (
          ""
        )}

        {artifactId && (contentData as SKArtifact).hasHistory ? <ContentHistoryNavigator title="Content Status" contentId={artifactId} /> : ""}
        {artifactId && context?.userRoles?.includes(UserRole.SkarvoAdmin) && contentData.reportCount && contentData.reportCount > 0 ? (
          <ContentReportNavigator title="Content Reports" />
        ) : (
          ""
        )}

        <AddTextField
          name={"addtitle"}
          title={"Add Title"}
          note={"Title can be the subject, purpose, theme or topic of your post."}
          placeholder={"Enter Title"}
          index={1}
          charAllowed={100}
          value={(contentData as SKArtifact).title}
          onChange={async e => {
            updateContentData({ title: e.target.value! });
          }}
          disabled={!isEditable}
          error={error?.title}
        />

        <AddTextAreaField
          name={"addtext"}
          title={"Add Text"}
          note={"Maximum text length of 500 characters."}
          placeholder={"Enter Text"}
          index={2}
          height={150}
          charAllowed={500}
          value={(contentData as SKArtifact).text as string}
          onChange={async e => {
            updateContentData({ text: e.target.value! });
          }}
          disabled={!isEditable}
          error={error?.text}
        />

        <ContextCheckListCard
          index={3}
          name="Add Topic"
          title="Choose the primary topic of your Content."
          purposeText="Add Topic"
          notes="Choose the topic that represents the primary focus of most of your Content."
          doneText={contentData.topic ? capitalizeFirstLetter(TopicTypeString[contentData.topic]) : undefined}
          action={() => {
            navigate("selectTopic");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={4}
          name="Add Age Rating"
          title="Choose the Age Rating of your Content"
          purposeText="Add Age Rating"
          notes="Choose the age rating based on the appropriateness of your Content for children."
          doneText={contentData.age ? "Age " + contentData.age + "+" : undefined}
          action={() => {
            navigate("ageRating");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={5}
          name="Certify Content"
          title="Certify Content"
          purposeText="Certify Content"
          notes="Certify that your Content would ensure and comply with these requirements."
          doneText={contentData.certifyFirst && contentData.certifySecond ? "Certified" : undefined}
          action={() => {
            navigate("certifyContent");
          }}
          disabled={!isEditable}
        />
        <PublishingTier index={6} />
      </div>
    </>
  );
});

export default TextArtifactContainer;
